import { useEffect, useRef, useState } from 'react';
import { useBasket } from '../context/BasketContext.jsx';
import { useCurrency } from '../context/CurrencyContext.jsx';
import { useStoreUI } from '../context/StoreUIContext.jsx';
import { useToast } from '../context/ToastContext.jsx';

const ADDED_FEEDBACK_MS = 1300;

export function PackageCard({ pkg, badge, priority = false }) {
  const { loggedIn, addPackage } = useBasket();
  const { openLogin, openPerks, openGift } = useStoreUI();
  const { formatPrice } = useCurrency();
  const { showToast } = useToast();

  const [isAdded, setAdded] = useState(false);
  const [isAdding, setAdding] = useState(false);
  const [imageSource, setImageSource] = useState(pkg.image_url || pkg.image_fallback_url || null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    setImageSource(pkg.image_url || pkg.image_fallback_url || null);
  }, [pkg.image_url, pkg.image_fallback_url]);

  const handleImageError = () => {
    if (pkg.image_fallback_url && imageSource !== pkg.image_fallback_url) {
      setImageSource(pkg.image_fallback_url);
      return;
    }

    setImageSource(null);
  };


  const handleAdd = async () => {
    if (!loggedIn) {
      openLogin();
      return;
    }

    if (isAdding) return;

    setAdding(true);
    let result;
    try {
      result = await addPackage(pkg.id);
    } catch {
      result = { success: false, error: 'The package could not be added. Please try again.' };
    } finally {
      setAdding(false);
    }

    if (!result.success) {
      if (result.requires_login) openLogin();
      else showToast(result.error || 'Could not add item.', 6000, 'error');
      return;
    }

    showToast(`${result.item?.name || pkg.name} added to basket!`, 3500);

    setAdded(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setAdded(false), ADDED_FEEDBACK_MS);
  };

  return (
    <div className="rank-card">
      {badge && <span className="rank-badge">{badge}</span>}
      {pkg.has_info !== false && (
        <button
          type="button"
          className="info-btn"
          aria-label={`More info about ${pkg.name}`}
          onClick={() => openPerks(pkg.id)}
        >
          <i className="fa-solid fa-circle-info" />
        </button>
      )}

      <div className="rank-icon-wrap">
        {imageSource ? (
          <img
            src={imageSource}
            className="rank-icon"
            alt={pkg.name}
            width="180"
            height="180"
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            decoding="async"
            onError={handleImageError}
          />
        ) : (
          <span className="rank-icon-placeholder" aria-hidden="true">
            <i className="fa-solid fa-box-open" />
          </span>
        )}
      </div>

      <h3>{pkg.name}</h3>
      {pkg.description && <p className="rank-description">{pkg.description}</p>}

      <div className="rank-price">{formatPrice(pkg.price, pkg.currency)}</div>

      <div className="rank-actions">
        <button type="button" className="gift-btn" aria-label={`Gift ${pkg.name}`} onClick={(event) => {
          if (!loggedIn) { openLogin(); return; }
          openGift(pkg, event.currentTarget);
        }}>
          <i className="fa-solid fa-gift" />
        </button>

        <button
          type="button"
          className={`cart-btn${isAdded ? ' added' : ''}${isAdding ? ' is-loading' : ''}`}
          aria-label={`${isAdding ? 'Adding' : isAdded ? 'Added' : 'Add'} ${pkg.name} to basket`}
          aria-busy={isAdding}
          disabled={isAdding}
          onClick={handleAdd}
        >
          <i className={`fa-solid ${isAdding ? 'fa-spinner fa-spin' : isAdded ? 'fa-check' : 'fa-cart-plus'}`} />
          <span>{isAdding ? 'Adding…' : isAdded ? 'Added' : 'Add to Basket'}</span>
        </button>
      </div>
    </div>
  );
}
